<template>
  <ModalComponent v-model:dialogVisible="showModal">
    <template #modalContent>
      <CropperComponent
        :imageUrl="imageUrl"
        :aspect-ratio="aspectRatio"
        :view-mode="viewMode"
        @cropped="handleCroppedImage"
        @cancel="handleCancel"
        @close-dialog="handleCloseDialog"
      />
    </template>
  </ModalComponent>
  <ModalComponent v-model:dialogVisible="staffModal">
    <template #modalContent>
      <AddStaffModal
        :staffModal="staffModal"
        :addedEmails="addedEmails"
        @addStaff="(email) => addedEmails.push(email)"
        @removeEmail="removeEmail"
        @submitEmails="submitEmails"
        @update:staffModal="staffModal = $event"
      />
    </template>
  </ModalComponent>
  <ModalComponent v-model:dialogVisible="subdomainModal">
    <template #modalContent>
      <div class="p-6 relative bg-white dark:bg-gray-800 rounded-lg shadow-lg">
        <!-- Modal Content -->
        <div
          class="w-full flex flex-col items-start justify-center p-4 rounded-lg border border-blue-gray-200 dark:border-gray-700"
        >
          <!-- Title and Warning -->
          <div class="flex flex-col items-start portrait:w-3/4 gap-2 mb-4">
            <p
              class="block antialiased font-semibold text-lg leading-tight text-gray-900 dark:text-gray-100"
            >
              Edit Subdomain
            </p>
            <div class="flex flex-row justify-center items-start gap-2">
              <i
                class="fa-solid fa-circle-exclamation text-yellow-500 dark:text-yellow-400 text-sm"
              ></i>
              <p class="text-sm text-gray-700 dark:text-gray-300">
                You can only change your subdomain once. Subsequent changes are
                not allowed.
              </p>
            </div>
          </div>

          <!-- Input Field with Save Button -->
          <div class="relative w-full h-14">
            <input
              type="text"
              placeholder="store1"
              v-model="subDomain"
              maxlength="20"
              @input="sanitizeSubdomain($event)"
              :class="{
                'peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-700 bg-transparent px-3 py-2.5 text-sm font-normal text-gray-900 dark:text-gray-100 outline-none transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-2 focus:border-gray-900 dark:focus:border-gray-100 focus:ring-0 pr-[85px]': true,
                'border-red-500 dark:border-red-400 text-red-600 dark:text-red-400 placeholder-red-500/60 dark:placeholder-red-400/60 bg-red-50 dark:bg-red-900/10':
                  subdomainError,
              }"
            />
            <div class="p-1 absolute right-0 inset-y-0">
              <button
                @click="saveSubdomain"
                type="button"
                class="bg-blue-gray-50 dark:bg-gray-800 rounded-r-lg px-3 py-2 h-full hover:bg-blue-gray-100 dark:hover:bg-gray-700 text-blue-gray-800 dark:text-gray-100 flex gap-2 items-center justify-center transition-colors"
              >
                <i class="fa-solid fa-save text-sm"></i>
                <span class="text-sm">Save</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Close Button -->
        <button
          @click="subdomainModal = false"
          class="!absolute top-3 right-3 h-8 w-8 flex items-center justify-center rounded-lg text-gray-800 dark:text-gray-200 hover:bg-gray-800/10 dark:hover:bg-gray-200/10 transition-colors"
          type="button"
        >
          <i class="fa-solid fa-xmark text-lg"></i>
        </button>
      </div>
    </template>
  </ModalComponent>
  <ModalComponent v-model:dialogVisible="addDomainModal">
    <template #modalContent>
      <div
        class="p-8 relative portrait:p-4 dark:bg-gray-800 dark:text-gray-100"
      >
        <div
          class="w-full flex flex-col items-start justify-center p-6 rounded-lg shadow-md bg-white dark:bg-gray-800 border border-blue-gray-200 dark:border-gray-700"
        >
          <!-- Header -->
          <div class="flex flex-col items-start gap-3 mb-6">
            <h2
              class="text-xl font-semibold leading-relaxed text-gray-900 dark:text-gray-100"
            >
              Custom Domain Management
            </h2>
            <p class="text-sm leading-6 text-gray-700 dark:text-gray-300">
              Connect your domain to your store at
              <strong class="text-main-600 dark:text-main-400">
                {{
                  subDomain
                    ? `https://${subDomain}.hisell.shop`
                    : "https://example.hisell.shop"
                }} </strong
              >.
            </p>
          </div>

          <!-- DNS Instructions -->
          <div
            class="flex flex-col gap-4 bg-blue-gray-50 dark:bg-gray-700 p-4 rounded-md border border-blue-gray-100 dark:border-gray-600 w-full"
          >
            <div class="flex items-start gap-3">
              <i
                class="fa-solid fa-circle-exclamation text-main-700 dark:text-main-400 text-lg"
              ></i>
              <p class="text-gray-800 dark:text-gray-200 text-sm leading-6">
                Follow these steps to connect your custom domain:
              </p>
            </div>

            <div class="text-sm text-gray-700 dark:text-gray-300 pl-6">
              <p>
                Go to your domain provider (e.g., GoDaddy, Namecheap,
                Cloudflare) and add the following
                <strong>CNAME records</strong>:
              </p>

              <ul class="list-disc list-inside ml-6 mt-3">
                <li><strong>Type:</strong> CNAME</li>
                <li><strong>Name / Host:</strong> <code>@</code></li>
                <li>
                  <strong>Value:</strong> <code>connect.tryhisell.com</code>
                </li>
                <li>
                  <strong>Proxy:</strong> DNS Only (disable the orange cloud if
                  using Cloudflare)
                </li>
              </ul>

              <ul class="list-disc list-inside ml-6 mt-4">
                <li><strong>Type:</strong> CNAME</li>
                <li><strong>Name / Host:</strong> <code>www</code></li>
                <li>
                  <strong>Value:</strong> <code>connect.tryhisell.com</code>
                </li>
                <li><strong>Proxy:</strong> DNS Only</li>
              </ul>

              <p class="mt-4">
                Once saved, click <strong>Activate</strong> below. SSL
                certificates will be generated automatically after DNS
                propagation completes.
              </p>
            </div>

            <div class="flex justify-end mt-3">
              <button
                @click="handleOpenVideo"
                class="text-white bg-main-700 hover:bg-main-800 dark:bg-main-600 dark:hover:bg-main-700 px-4 py-2 rounded-md text-sm"
              >
                <i class="fa-solid fa-play-circle mr-2"></i> Watch Setup Video
              </button>
            </div>
          </div>

          <!-- Domain Input -->
          <div class="w-full mt-6 relative">
            <label
              for="customDomain"
              class="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-2"
            >
              Enter Domain (e.g., example.com)
            </label>

            <div class="relative w-full">
              <input
                id="customDomain"
                type="text"
                placeholder="e.g., example.com"
                v-model="customDomain"
                maxlength="40"
                pattern="[a-z0-9.-]*"
                @input="sanitizeDomain($event)"
                :class="{
                  'peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-100 outline-none transition-all focus:border-2 focus:border-gray-900 dark:focus:border-gray-100 pr-[85px]': true,
                  '!border-red-500 !text-red-600 dark:!border-red-400 dark:!text-red-400':
                    customdomainError,
                }"
              />

              <div class="absolute right-0 inset-y-0 flex items-center">
                <button
                  @click="saveCustomDomain"
                  type="button"
                  :disabled="isSaving"
                  class="bg-blue-gray-50 dark:bg-gray-700 rounded-r-lg px-3 py-2 h-full hover:bg-blue-gray-100 dark:hover:bg-gray-600 text-blue-gray-800 dark:text-gray-200 flex gap-1 items-center justify-center"
                >
                  <span v-if="isSaving">
                    <svg
                      class="w-4 h-4 animate-spin text-gray-900/50 dark:text-gray-100/50"
                      viewBox="0 0 64 64"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                    >
                      <path
                        d="M32 3C35.8083 3 39.5794 3.75011 43.0978 5.20749C46.6163 6.66488 49.8132 8.80101 52.5061 11.4939C55.199 14.1868 57.3351 17.3837 58.7925 20.9022C60.2499 24.4206 61 28.1917 61 32C61 35.8083 60.2499 39.5794 58.7925 43.0978C57.3351 46.6163 55.199 49.8132 52.5061 52.5061C49.8132 55.199 46.6163 57.3351 43.0978 58.7925C39.5794 60.2499 35.8083 61 32 61C28.1917 61 24.4206 60.2499 20.9022 58.7925C17.3837 57.3351 14.1868 55.199 11.4939 52.5061C8.801 49.8132 6.66487 46.6163 5.20749 43.0978C3.7501 39.5794 3 35.8083 3 32C3 28.1917 3.75011 24.4206 5.2075 20.9022C6.66489 17.3837 8.80101 14.1868 11.4939 11.4939C14.1868 8.80099 17.3838 6.66487 20.9022 5.20749C24.4206 3.7501 28.1917 3 32 3Z"
                        stroke="currentColor"
                        stroke-width="5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M32 3C36.5778 3 41.0906 4.08374 45.1692 6.16256C49.2477 8.24138 52.7762 11.2562 55.466 14.9605C58.1558 18.6647 59.9304 22.9531 60.6448 27.4748C61.3591 31.9965 60.9928 36.6232 59.5759 40.9762"
                        stroke="currentColor"
                        stroke-width="5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="text-gray-900 dark:text-gray-100"
                      />
                    </svg>
                  </span>
                  <span v-else><i class="fa-solid fa-bolt"></i> Activate</span>
                </button>
              </div>
            </div>

            <div
              v-if="progressMessage"
              class="mt-4 text-sm text-main-700 dark:text-main-400"
            >
              <strong>Progress:</strong> {{ progressMessage }}
            </div>

            <p
              v-if="customdomainError"
              class="text-sm text-red-600 dark:text-red-400 mt-2"
            >
              Please enter a valid domain.
            </p>
          </div>
        </div>

        <!-- Close Button -->
        <button
          @click="addDomainModal = false"
          class="absolute top-3 right-3 h-8 w-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition"
          type="button"
        >
          <i class="fa-solid fa-xmark text-lg"></i>
        </button>
      </div>
    </template>
  </ModalComponent>

  <div
    v-if="storeDetails"
    :key="refreshKey"
    class="flex flex-col items-center w-full gap-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl shadow-blue-gray-900/5 bg-clip-border"
  >
    <VideoDropdownModal v-model:open="showVideoModal" :video-url="videoURL" />
    <div
      class="relative flex flex-col w-full font-regular p-8 portrait:px-2 portrait:py-5 py-10 text-gray-900 dark:text-gray-200 max-w-5xl"
    >
      <nav
        class="w-full flex flex-row portrait:flex-col justify-between portrait:gap-5 mb-6 portrait:items-start items-center"
      >
        <div
          class="w-full max-w-fit flex flex-row gap-5 items-start justify-start portrait:items-start landscape:mr-2"
        >
          <button
            @click="goback"
            class="px-4 flex flex-col items-center justify-center aspect-square w-fit bg-blue-gray-500/10 hover:bg-blue-gray-500/20 border-blue-gray-600/10 rounded-lg dark:bg-gray-700 dark:hover:bg-gray-600 transition-all duration-300 ease-in-out transform hover:scale-105"
            aria-label="Go back"
          >
            <i class="fa-solid fa-arrow-left text-base dark:text-gray-200"></i>
          </button>
          <div>
            <h3
              class="text-[clamp(18px,2vw,24px)] font-medium dark:text-gray-200"
            >
              Store Settings
            </h3>
            <p
              class="text-[14px] font-normal leading-6 text-gray-800 opacity-80 dark:text-gray-400"
            >
              Customize your site to look and respond how you want
            </p>
          </div>
        </div>
        <button
          @click="handleOpenVideo"
          class="w-full landscape:max-w-fit flex gap-2 items-center justify-center px-5 py-4 bg-blue-gray-500/10 rounded-lg text-[14px] border-blue-gray-600/10 font-medium h-min dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-600 dark:text-gray-200 transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-lg hover:bg-blue-gray-500/30"
        >
          <i
            class="fa-regular fa-circle-play dark:text-gray-200 transition-transform duration-300 ease-in-out transform hover:rotate-12"
          ></i
          ><span>Watch Video Tutorial</span>
        </button>
      </nav>
      <div
        class="w-full border-[1px] bg-white dark:bg-gray-700 border-gray-900/10 flex flex-col h-full rounded-lg relative"
      >
        <h4
          class="text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 p-6 inline-flex rounded-t-lg text-[18px] font-medium"
        >
          Basic Store Information
        </h4>
        <form class="flex flex-col gap-4 mt-4 py-6">
          <div
            class="mb-4 px-6"
            :class="{
              'border-l border-sub-900': editedFields.storeNameValue,
            }"
          >
            <div class="flex flex-col items-start portrait:w-3/4 gap-1 mb-2">
              <p
                class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
              >
                Store Name<span class="text-red-900 text-[14px]">*</span>
              </p>
              <div class="flex flex-row justify-start items-start gap-1">
                <i
                  class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                ></i>
                <p class="text-xs">
                  Do not separate compound names like "Techworld."
                </p>
              </div>
            </div>
            <div class="relative h-14 w-full">
              <input
                type="text"
                placeholder="Abc stores"
                v-model="storeFormData['storeNameValue']"
                @blur="validateLength($event, 'storeNameValue')"
                :class="{
                  'peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700': true,
                  'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                    storeNameError,
                }"
              />
            </div>
          </div>
          <div
            class="mb-4 px-6"
            :class="{
              'border-l border-sub-900': editedFields.storeDiscriptionValue,
            }"
          >
            <p
              class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
            >
              Store Description<span
                class="text-red-900 dark:text-red-500 text-[14px]"
                >*</span
              >
            </p>
            <div class="relative w-full">
              <textarea
                placeholder="We sell a lot of things"
                v-model="storeFormData['storeDiscriptionValue']"
                @blur="validateLength($event, 'storeDiscriptionValue')"
                :class="{
                  'peer h-full w-full min-h-28 rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 outline dark:text-gray-200 outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700': true,
                  'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                    storeDiscriptionError,
                }"
              ></textarea>
            </div>
          </div>
          <div
            class="mb-4 px-6"
            :class="{
              'border-l border-sub-900': editedFields.storeEmailValue,
            }"
          >
            <p
              class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
            >
              Store Email<span
                class="text-red-900 dark:text-red-500 text-[14px]"
                >*</span
              >
            </p>
            <div class="relative h-14 w-full">
              <input
                type="text"
                placeholder="example@eg.com"
                v-model="storeFormData['storeEmailValue']"
                @input="
                  (e) =>
                    handleEditedField(
                      e.target.value.trim() !== '',
                      'storeEmailValue'
                    )
                "
                :class="{
                  'peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700': true,
                  'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                    emailError,
                }"
              />
            </div>
          </div>
          <div
            class="mb-4 px-6"
            :class="{
              'border-l border-sub-900': editedFields.storeTaglineValue,
            }"
          >
            <p
              class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
            >
              Store Tagline (Optional)
            </p>
            <div class="relative h-14 w-full">
              <input
                type="text"
                placeholder="We are the best"
                v-model="storeFormData['storeTaglineValue']"
                @input="
                  (e) =>
                    handleEditedField(
                      e.target.value.trim() !== '',
                      'storeTaglineValue'
                    )
                "
                :class="{
                  'peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700': true,
                  'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                    taglineError,
                }"
              />
            </div>
          </div>
          <h4
            class="text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 p-6 inline-flex text-[18px] font-medium"
          >
            Store URL
          </h4>
          <div class="mt-4 mb-1 px-6 w-full">
            <div
              class="flex items-center justify-between mb-4 portrait:flex-col portrait:items-start"
            >
              <div
                class="flex flex-col landscape:w-1/2 items-start w-full gap-1 portrait:mb-4"
              >
                <p
                  class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Store Web Address
                </p>
                <div class="flex flex-row justify-center items-start gap-1">
                  <i
                    class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                  ></i>
                  <p class="text-sm">
                    You have a <strong>.hisell.shop</strong> domain. and you can
                    customize this to something unique.
                  </p>
                </div>
              </div>
              <div
                class="flex flex-row justify-end items-center gap-4 portrait:w-full"
              >
                <router-link
                  to="/buy-domain"
                  title="You can buy domain from us"
                  class="portrait:w-full align-middle select-none font-semibold text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-base portrait:text-sm py-2 px-4 rounded-lg bg-button dark:bg-sub-800 text-white shadow-md shadow-gray-900/10 hover:shadow-lg hover:shadow-main-200 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none"
                >
                  Buy Domain
                </router-link>
                <button
                  @click="addDomainModal = true"
                  :disabled="!domainEdit && hasDomain"
                  type="button"
                  title="Connect your existing domain"
                  class="portrait:w-full rounded-lg border border-main-900 dark:border-white py-2 px-4 text-center portrait:text-sm align-middle text-base font-semibold text-main-900 dark:text-white transition-all hover:opacity-75 active:opacity-[0.85] disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
                >
                  Add Domain
                </button>
              </div>
            </div>
            <div
              class="flex items-center justify-center w-full gap-2 bg-main-50 dark:bg-blue-gray-50/20 p-4 rounded-lg relative"
            >
              <div
                class="flex items-center w-full pr-[85px] portrait:flex-col portrait:items-start gap-1"
              >
                <p
                  class="block font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Your HiSell URL:
                </p>
                <a
                  :href="
                    subDomain
                      ? `https://${subDomain}.hisell.shop`
                      : 'https://example.hisell.shop'
                  "
                  v-text="
                    subDomain
                      ? `https://${subDomain}.hisell.shop`
                      : 'https://example.hisell.shop'
                  "
                  target="_blank"
                  rel="noopener noreferrer"
                  class="block font-medium leading-normal text-main-900 dark:text-sub-400"
                ></a>
              </div>
              <div
                class="px-4 absolute right-0 inset-y-0 h-full flex items-center justify-center"
              >
                <button
                  class="flex items-center gap-2 portrait:w-full align-middle select-none font-semibold justify-center text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-base portrait:text-sm py-2 px-3 rounded-lg bg-button dark:bg-sub-800 text-white shadow-md shadow-gray-900/10 hover:shadow-lg hover:shadow-main-200 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none whitespace-nowrap"
                  type="button"
                  :disabled="isSubdomainEdited"
                  @click="subdomainModal = true"
                >
                  Edit
                </button>
              </div>
            </div>
          </div>
          <div
            class="mt-4 mb-4 px-6"
            :class="{
              'border-l border-sub-900': editedFields.storeUrl,
            }"
          >
            <div class="flex items-center justify-between mb-2 pr-4">
              <div class="flex flex-col items-start portrait:w-3/4 gap-1">
                <p
                  class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Use Custom Domain
                </p>
                <div class="flex flex-row justify-center items-start gap-1">
                  <i
                    class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                  ></i>
                  <p class="text-sm">
                    Enable this to allow users access your store through your
                    custom domain.
                  </p>
                </div>
              </div>
              <div
                class="relative inline-block w-8 h-6 rounded-full cursor-pointer"
              >
                <input
                  v-if="storeFormData.storeUrl?.custom_domain"
                  id="customDomainSwitch"
                  type="checkbox"
                  class="absolute w-8 h-6 transition-colors duration-300 rounded-full appearance-none cursor-pointer peer bg-blue-gray-100 checked:bg-main-900 peer-checked:border-main-900 peer-checked:before:bg-main-900 dark:checked:bg-sub-900 dark:peer-checked:border-sub-900 dark:peer-checked:before:bg-sub-900"
                  v-model="storeFormData.storeUrl.custom_domain.status"
                  @input="
                    storeFormData.storeUrl.custom_domain.link
                      ? handleEditedField(true, 'storeUrl')
                      : handleEditedField(false, 'storeUrl')
                  "
                />
                <label
                  htmlFor="customDomainSwitch"
                  class="before:content[''] absolute top-2/4 -left-2 h-6 w-6 -translate-y-2/4 cursor-pointer rounded-full border border-blue-gray-100 bg-white shadow-md transition-all duration-300 before:absolute before:top-2/4 before:left-2/4 before:block before:h-10 before:w-10 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity hover:before:opacity-10 peer-checked:translate-x-full peer-checked:border-main-900 peer-checked:before:bg-main-900"
                >
                  <div
                    class="inline-block p-5 rounded-full top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4"
                    data-ripple-dark="true"
                  ></div>
                </label>
              </div>
            </div>
            <div v-if="isDomainEnabled">
              <div
                v-if="!hasDomain"
                class="w-full p-5 flex items-center justify-center gap-2"
              >
                <i class="fa-solid fa-ban"></i>
                <span>No domain added</span>
              </div>
              <div v-else class="relative w-full h-14">
                <div class="p-1 absolute right-0 inset-y-0">
                  <button
                    :disabled="!hasDomain"
                    @click="domainEdit = true"
                    type="button"
                    class="bg-blue-gray-50 rounded-r-lg px-4 py-3 h-full hover:bg-blue-gray-100 text-blue-gray-800 flex gap-1 items-center justify-center"
                  >
                    <i class="fa-solid fa-pen"></i>Edit
                  </button>
                </div>
                <input
                  disabled
                  type="text"
                  placeholder="https://your-store.hisell.shop"
                  v-model="domain"
                  @input="handleEditedField(true, 'storeUrl')"
                  :class="{
                    'peer h-full w-full rounded-lg border border-blue-gray-200 border-t-transparent !border-t-blue-gray-200 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:border-2 focus:border-gray-900 focus:border-t-transparent focus:!border-t-gray-900 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 pr-[85px]': true,
                    'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                      domainError,
                  }"
                />
                <span v-if="domainError" class="text-red-600 text-sm"
                  >Invalid domain entered.</span
                >
              </div>
            </div>
          </div>
          <h4
            class="text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 p-6 inline-flex text-[18px] font-medium"
          >
            Store Branding
          </h4>
          <div
            class="mb-4 px-6 pt-6"
            :class="{
              'border-l border-sub-900': editedFields.deletedBanner?.logo,
            }"
          >
            <div
              class="w-full flex flex-col items-start justify-center p-4 rounded-lg border border-blue-gray-50"
            >
              <div class="mb-4 flex flex-col items-start m-0 p-0">
                <p
                  class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Store Logo<span class="text-red-900 text-[14px]">*</span>
                </p>
                <p class="text-xs">
                  <i
                    class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                  ></i>
                  Max file size: 5mb (Recommended size: 512x512px)
                </p>
              </div>
              <div class="relative w-full h-auto">
                <div class="flex flex-col gap-1 mb-2">
                  <p v-if="exceedImageFileSize" class="text-red-800 text-sm">
                    Maximum file size: <strong>{{ maxFileSize }} MB</strong>
                  </p>
                  <p v-if="invalidLogoFileType" class="text-red-800 text-sm">
                    Allowed file types: <strong>jpg, png</strong>
                  </p>
                </div>
                <div class="flex flex-wrap gap-2">
                  <div
                    v-if="storeFormData['logoFile']"
                    class="w-[80px] aspect-square rounded-lg border-dashed border-main-800 border-sub-800 flex flex-col items-center justify-center border relative p-1"
                  >
                    <img
                      :src="getFileUrl(storeFormData['logoFile'])"
                      @error="
                        (event) =>
                          (event.target.src = '/landscape-placeholder.svg')
                      "
                      class="h-full w-full object-cover object-center rounded-lg"
                    />
                    <button
                      @click="removeLogoImage"
                      data-dismissible-target="alert"
                      class="!absolute p-2 bg-white/80 h-6 w-6 top-2 right-2 select-none rounded-lg text-center align-middle font-medium uppercase text-gray-800 transition-all hover:bg-gray-800/20 active:bg-gray-800/30 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none flex flex-col justify-center items-center"
                      type="button"
                    >
                      <i
                        class="fa-solid fa-trash text-[16px] text-main-900"
                      ></i>
                    </button>
                  </div>
                  <label
                    v-if="storeFormData['logoFile'] === ''"
                    class="w-[80px] aspect-square rounded-lg border-dashed border-gray-400 flex flex-col items-center justify-center border cursor-pointer"
                  >
                    <i class="fa-solid fa-plus text-gray-400"></i>
                    <input
                      type="file"
                      accept="image/*"
                      class="hidden"
                      @change="handleLogoUpload"
                      @input="handleEditedField(true, 'logoFile')"
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div
            class="mb-4 px-6"
            :class="{
              'border-l border-sub-900': isPortraitBannerEdited,
            }"
          >
            <div
              class="w-full flex flex-col items-start justify-center p-4 rounded-lg border border-blue-gray-50"
            >
              <div class="mb-4 flex flex-col items-start m-0 p-0">
                <p
                  class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Store Portrait Banner<span class="text-red-900 text-[14px]"
                    >*</span
                  >
                </p>
                <p class="text-xs">
                  <i
                    class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                  ></i>
                  Max file size: 5mb (Recommended size: 720x1280px)
                </p>
              </div>
              <div class="relative w-full h-auto">
                <div class="flex flex-col gap-1 mb-2">
                  <p v-if="exceedPortraitMaxCount" class="text-red-800 text-sm">
                    Maximum number of pictures for your current plan is
                    <strong>{{ maxCount }}</strong
                    >.
                    <RouterLink
                      ><button
                        class="px-2 py-1 bg-blue-gray-500/10 hover:bg-blue-gray-500/20 rounded text-[14px] border-bluegray-600/10 font-medium text-sm text-gray-800"
                      >
                        Upgrade Plan
                      </button></RouterLink
                    >
                  </p>
                  <p v-if="exceedPortraitFileSize" class="text-red-800 text-sm">
                    Maximum file size: <strong>{{ maxFileSize }} MB</strong>
                  </p>
                  <p
                    v-if="invalidPortraitFileType"
                    class="text-red-800 text-sm"
                  >
                    Allowed file types: <strong>jpg, jpeg & png</strong>
                  </p>
                </div>
                <div class="flex flex-wrap gap-2">
                  <div
                    v-for="(file, index) in storeFormData.storeBanner?.portrait"
                    :key="index"
                    class="h-[80px] aspect-[9/16] rounded-lg border-dashed border-main-800 border-sub-800 flex flex-col items-center justify-center border relative p-1"
                  >
                    <img
                      :src="getFileUrl(file)"
                      @error="
                        (event) =>
                          (event.target.src = '/landscape-placeholder.svg')
                      "
                      class="h-full w-full object-cover object-center rounded-lg"
                    />
                    <button
                      @click="removePortraitImage(index)"
                      data-dismissible-target="alert"
                      class="!absolute p-2 bg-white/80 h-6 w-6 top-2 right-2 select-none rounded-lg text-center align-middle font-medium uppercase text-gray-800 transition-all hover:bg-gray-800/20 active:bg-gray-800/30 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none flex flex-col justify-center items-center"
                      type="button"
                    >
                      <i
                        class="fa-solid fa-trash text-[16px] text-main-900"
                      ></i>
                    </button>
                  </div>
                  <label
                    v-if="
                      (storeFormData.storeBanner?.portrait || []).length <
                      maxCount
                    "
                    class="w-[80px] aspect-square rounded-lg border-dashed border-gray-400 flex flex-col items-center justify-center border cursor-pointer"
                  >
                    <i class="fa-solid fa-plus text-gray-400"></i>
                    <input
                      type="file"
                      accept="image/*"
                      class="hidden"
                      @change="handlePortraitUpload"
                      @input="
                        handleEditedField(true, 'storeBanner', 'portrait')
                      "
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div
            class="mb-4 px-6"
            :class="{
              'border-l border-sub-900':
                editedFields.storeBanner?.landscape ||
                editedFields.deletedBanner?.landscape,
            }"
          >
            <div
              class="w-full flex flex-col items-start justify-center p-4 rounded-lg border border-blue-gray-50"
            >
              <div class="mb-4 flex flex-col items-start m-0 p-0">
                <p
                  class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Store Landscape Banner<span class="text-red-900 text-[14px]"
                    >*</span
                  >
                </p>
                <p class="text-xs">
                  <i
                    class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                  ></i>
                  Max file size: 5mb (Recommended size: 1280x720px)
                </p>
              </div>
              <div class="relative w-full h-auto">
                <div class="flex flex-col gap-1 mb-2">
                  <p
                    v-if="exceedLandscapeMaxCount"
                    class="text-red-800 text-sm"
                  >
                    Maximum number of pictures for your current plan is
                    <strong>{{ maxCount }}</strong
                    >.
                    <RouterLink
                      ><button
                        class="px-2 py-1 bg-blue-gray-500/10 hover:bg-blue-gray-500/20 rounded text-[14px] border-bluegray-600/10 font-medium text-sm text-gray-800"
                      >
                        Upgrade Plan
                      </button></RouterLink
                    >
                  </p>
                  <p
                    v-if="exceedLandscapeFileSize"
                    class="text-red-800 text-sm"
                  >
                    Maximum file size: <strong>{{ maxFileSize }} MB</strong>
                  </p>
                  <p
                    v-if="invalidLandscapeFileType"
                    class="text-red-800 text-sm"
                  >
                    Allowed file types: <strong>jpg, jpeg & png</strong>
                  </p>
                </div>
                <div class="flex flex-wrap gap-2">
                  <div
                    v-for="(file, index) in storeFormData.storeBanner
                      ?.landscape"
                    :key="index"
                    class="h-[80px] aspect-[16/9] rounded-lg border-dashed border-main-800 border-sub-800 flex flex-col items-center justify-center border relative p-1"
                  >
                    <img
                      :src="getFileUrl(file)"
                      @error="
                        (event) =>
                          (event.target.src = '/landscape-placeholder.svg')
                      "
                      class="h-full w-full object-cover object-center rounded-lg"
                    />
                    <button
                      @click="removeLandscapeImage(index)"
                      data-dismissible-target="alert"
                      class="!absolute p-2 bg-white/80 h-6 w-6 top-2 right-2 select-none rounded-lg text-center align-middle font-medium uppercase text-gray-800 transition-all hover:bg-gray-800/20 active:bg-gray-800/30 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none flex flex-col justify-center items-center"
                      type="button"
                    >
                      <i
                        class="fa-solid fa-trash text-[16px] text-main-900"
                      ></i>
                    </button>
                  </div>
                  <label
                    v-if="
                      storeFormData.storeBanner?.landscape.length < maxCount
                    "
                    class="w-[80px] aspect-square rounded-lg border-dashed border-gray-400 flex flex-col items-center justify-center border cursor-pointer"
                  >
                    <i class="fa-solid fa-plus text-gray-400"></i>
                    <input
                      type="file"
                      accept="image/*"
                      class="hidden"
                      @change="handleLandscapeUpload"
                      @input="
                        handleEditedField(true, 'storeBanner', 'landscape')
                      "
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
          <h4
            class="text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 p-6 inline-flex text-[18px] font-medium"
          >
            Store Staffs
          </h4>
          <div class="mt-4 mb-4 px-6 w-full">
            <div
              class="flex items-center justify-between mb-5 portrait:flex-col portrait:items-start"
            >
              <div
                class="flex flex-col landscape:w-1/2 items-start w-full gap-1 portrait:mb-4"
              >
                <p
                  class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Staff Accounts
                </p>
                <div class="flex flex-row justify-center items-start gap-1">
                  <i
                    class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                  ></i>
                  <p class="text-sm">
                    Manage accounts with access to your store.
                  </p>
                </div>
              </div>
              <div
                class="flex flex-row justify-end items-center gap-4 portrait:w-full"
              >
                <div
                  class="w-full landscape:max-w-sm landscape:min-w-[200px] relative"
                >
                  <div class="relative w-full">
                    <input
                      type="text"
                      placeholder="Search staffs"
                      class="peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 pl-3 py-3 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700 pr-11"
                    />
                    <button
                      class="absolute h-8 w-8 right-1.5 top-1.5 my-auto px-2 flex items-center bg-main-800 dark:bg-sub-800 rounded-lg hover:bg-gray-700 dark:hover:bg-gray-500"
                      type="button"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="#FFF"
                        class="w-8 h-8"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <button
                  class="flex items-center gap-2 portrait:w-full align-middle select-none font-semibold justify-center text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-base portrait:text-sm py-2 px-3 rounded-lg bg-button dark:bg-sub-800 text-white shadow-md shadow-gray-900/10 hover:shadow-lg hover:shadow-main-200 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none whitespace-nowrap"
                  type="button"
                  @click="staffModal = true"
                >
                  <i class="fa-solid fa-plus text-sm"></i>
                  Add Staff
                </button>
              </div>
            </div>
            <div class="relative w-full h-auto">
              <div
                class="w-full h-full overflow-auto border rounded-lg border-blue-gray-100"
              >
                <table class="w-full text-left table-auto">
                  <thead>
                    <tr class="p-4 bg-blue-gray-50/50 dark:bg-blue-gray-50/20">
                      <th class="p-4">
                        <p
                          class="block text-sm antialiased font-normal leading-none text-blue-gray-900 dark:text-white opacity-70"
                        >
                          Staff Name
                        </p>
                      </th>
                      <th class="p-4">
                        <p
                          class="block text-sm antialiased font-normal leading-none text-blue-gray-900 dark:text-white opacity-70"
                        >
                          Orders Recorded
                        </p>
                      </th>
                      <th class="p-4">
                        <p
                          class="block text-sm antialiased font-normal leading-none text-blue-gray-900 dark:text-white opacity-70"
                        >
                          Status
                        </p>
                      </th>
                      <th class="p-4">
                        <p
                          class="block text-sm antialiased font-normal leading-none text-blue-gray-900 dark:text-white opacity-70"
                        >
                          Last Login
                        </p>
                      </th>
                      <th class="p-4">
                        <p
                          class="block text-sm antialiased font-normal leading-none text-blue-gray-900 dark:text-white opacity-70"
                        ></p>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-if="staffs.length">
                      <tr v-for="staff in staffs" :key="staff.id">
                        <td class="p-4 border-b border-blue-gray-50">
                          <div class="flex items-center gap-3">
                            <RouterLink
                              :to="`/staff/${staff.id}`"
                              class="block text-sm antialiased font-normal leading-normal text-blue-gray-900 dark:text-white underline"
                            >
                              {{ staff.name }}
                            </RouterLink>
                          </div>
                        </td>
                        <td class="p-4 border-b border-blue-gray-50">
                          <div class="flex flex-col">
                            <p
                              class="block text-sm antialiased font-normal leading-normal text-blue-gray-900 dark:text-white"
                            >
                              {{ staff.orders_recorded }}
                            </p>
                            <p
                              class="block text-sm antialiased font-normal leading-normal text-blue-gray-900 dark:text-white opacity-70"
                            >
                              Orders
                            </p>
                          </div>
                        </td>
                        <td class="p-4 border-b border-blue-gray-50">
                          <div class="w-max">
                            <div
                              :class="{
                                'relative grid items-center px-2 py-1 text-xs font-bold text-main-900 uppercase rounded-xl select-none whitespace-nowrap bg-main-50':
                                  staff.status === 1,
                                'relative grid items-center px-2 py-1 text-xs font-bold text-red-600 uppercase rounded-xl select-none whitespace-nowrap bg-main-50':
                                  staff.status === 0,
                              }"
                            >
                              <span>{{
                                staff.status === 1 ? "Approved" : "Pending"
                              }}</span>
                            </div>
                          </div>
                        </td>
                        <td class="p-4 border-b border-blue-gray-50">
                          <div class="flex flex-col w-max gap-1">
                            <p
                              class="block text-sm antialiased font-normal leading-normal text-blue-gray-900 dark:text-white"
                            >
                              {{ staff.last_login }}
                            </p>
                          </div>
                        </td>
                        <td class="p-4 border-b border-blue-gray-50">
                          <button
                            class="relative h-10 max-h-[40px] w-max select-none rounded-lg text-center align-middle text-xs font-medium uppercase text-red-900 transition-all hover:bg-gray-900/10 active:bg-gray-900/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none flex flex-col items-start justify-center"
                            type="button"
                            @click="deleteStaff(staff.id)"
                          >
                            <i class="fa-solid fa-trash-can text-base"></i>
                          </button>
                        </td>
                      </tr>
                    </template>
                    <template v-else>
                      <tr>
                        <td
                          class="p-4 border-b border-blue-gray-50 text-center"
                          colspan="5"
                        >
                          <p class="text-sm text-blue-gray-900 dark:text-white">
                            No staff added.
                          </p>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <h4
            class="text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 p-6 inline-flex text-[18px] font-medium"
          >
            StoreFront Customization
          </h4>
          <div
            class="my-4 px-6"
            :class="{
              'border-l border-sub-900': editedFields.active,
            }"
          >
            <div class="flex flex-col items-start portrait:w-3/4 gap-1 mb-4">
              <p
                class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
              >
                Store Themes
              </p>
              <div class="flex flex-row justify-center items-start gap-1">
                <i
                  class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                ></i>
                <p class="text-xs">
                  Select a theme that defines your business, Choose one that
                  best tells your brand's story.
                </p>
              </div>
            </div>
            <div class="relative w-full">
              <div class="flex flex-wrap w-full gap-4">
                <div
                  v-for="theme in showAllThemes
                    ? sortedThemes
                    : sortedThemes.slice(0, 2)"
                  :key="theme.id"
                  class="relative flex flex-col bg-white dark:bg-blue-gray-50/20 shadow-sm border dark:border-gray-500 border-gray-200 rounded-lg landscape:w-[calc(50%-16px)] w-full md:w-[calc(50%-16px)]"
                >
                  <div
                    class="relative h-max m-2.5 overflow-hidden text-white rounded-lg"
                  >
                    <img
                      :src="theme.banner || '/landscape-placeholder.svg'"
                      alt="card-image"
                      @error="
                        (event) =>
                          (event.target.src = '/landscape-placeholder.svg')
                      "
                    />
                  </div>
                  <div class="p-4">
                    <div class="flex items-center mb-2">
                      <h6
                        class="text-slate-800 text-xl font-semibold text-nowrap overflow-hidden text-ellipsis"
                      >
                        {{ theme.name }}
                      </h6>

                      <div class="flex items-center gap-0 5 ml-auto">
                        <i class="fa-solid fa-star text-xl text-yellow-600"></i>
                        <span class="text-slate-600 ml-1.5">5.0</span>
                      </div>
                    </div>

                    <p
                      class="text-slate-600 leading-normal font-light line-clamp-4 text-ellipsis overflow-hidden"
                    >
                      {{ theme.description }}
                    </p>
                  </div>
                  <div class="px-4 pb-4 pt-0 mt-2">
                    <button
                      :disabled="storeFormData.active == theme.id"
                      @click="activateTheme(theme.id)"
                      class="w-full rounded-lg bg-main-800 dark:bg-sub-800 py-2 px-4 border border-transparent text-center text-sm text-white transition-all shadow-md hover:shadow-lg focus:bg-main-700 dark:focus:bg-sub-700 focus:shadow-none active:bg-main-700 hover:bg-main-700 dark:active:bg-sub-700 dark:hover:bg-sub-700 active:shadow-none disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
                      type="button"
                    >
                      {{
                        storeFormData.active == theme.id
                          ? "Activated"
                          : "Activate Theme"
                      }}
                    </button>
                  </div>
                </div>
              </div>
              <button
                v-if="!showAllThemes && sortedThemes.length > 2"
                @click="showAllThemes = true"
                class="select-none rounded-lg border border-main-900 dark:border-sub-900 py-3 px-4 mt-4 text-center portrait:text-sm align-middle text-base font-semibold text-main-900 dark:text-sub-900 transition-all hover:opacity-75 active:opacity-[0.85] disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none w-full"
              >
                Show All themes
              </button>
            </div>
          </div>
          <h4
            class="text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 p-6 inline-flex text-[18px] font-medium"
          >
            Store Policies
          </h4>
          <div
            class="mt-4 px-6"
            :class="{
              'border-l border-sub-900': editedFields.policies?.privacypolicy,
            }"
          >
            <div class="flex items-center justify-between mb-2 pr-4">
              <div class="flex flex-col items-start portrait:w-3/4 gap-1 mb-4">
                <p
                  class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Privacy Policy
                </p>
                <div class="flex flex-row justify-center items-start gap-1">
                  <i
                    class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                  ></i>
                  <p class="text-xs">Set your store user privacy policy</p>
                </div>
              </div>
              <div
                class="relative inline-block w-8 h-6 rounded-full cursor-pointer"
              >
                <input
                  v-if="storeFormData.policies"
                  id="privacyPolicySwitch"
                  type="checkbox"
                  class="absolute w-8 h-6 transition-colors duration-300 rounded-full appearance-none cursor-pointer peer bg-blue-gray-100 checked:bg-main-900 peer-checked:border-main-900 peer-checked:before:bg-main-900 dark:checked:bg-sub-900 dark:peer-checked:border-sub-900 dark:peer-checked:before:bg-sub-900"
                  v-model="storeFormData.policies.privacypolicy.status"
                  @input="
                    storeFormData.policies.privacypolicy.text
                      ? handleEditedField(true, 'policies', 'privacypolicy')
                      : handleEditedField(false, 'policies', 'privacypolicy')
                  "
                />
                <label
                  htmlFor="privacyPolicySwitch"
                  class="before:content[''] absolute top-2/4 -left-2 h-6 w-6 -translate-y-2/4 cursor-pointer rounded-full border border-blue-gray-100 bg-white shadow-md transition-all duration-300 before:absolute before:top-2/4 before:left-2/4 before:block before:h-10 before:w-10 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity hover:before:opacity-10 peer-checked:translate-x-full peer-checked:border-main-900 peer-checked:before:bg-main-900"
                >
                  <div
                    class="inline-block p-5 rounded-full top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4"
                    data-ripple-dark="true"
                  ></div>
                </label>
              </div>
            </div>
            <div class="relative w-full">
              <textarea
                v-if="storeFormData.policies"
                placeholder="We respect your privacy policy"
                v-model="storeFormData.policies.privacypolicy.text"
                @input="handleEditedField(true, 'policies', 'privacypolicy')"
                :class="{
                  'peer h-full w-full min-h-28 rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 outline dark:text-gray-200 outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700': true,
                  'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                    privacyPolicyError,
                }"
              ></textarea>
            </div>
          </div>
          <div
            class="mt-4 px-6"
            :class="{
              'border-l border-sub-900': editedFields.policies?.returnpolicy,
            }"
          >
            <div class="flex items-center justify-between mb-2 pr-4">
              <div class="flex flex-col items-start portrait:w-3/4 gap-1 mb-4">
                <p
                  class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Return Policy
                </p>
                <div class="flex flex-row justify-center items-start gap-1">
                  <i
                    class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                  ></i>
                  <p class="text-xs">Set your store return policy</p>
                </div>
              </div>
              <div
                class="relative inline-block w-8 h-6 rounded-full cursor-pointer"
              >
                <input
                  v-if="storeFormData.policies"
                  id="returnPolicySwitch"
                  type="checkbox"
                  class="absolute w-8 h-6 transition-colors duration-300 rounded-full appearance-none cursor-pointer peer bg-blue-gray-100 checked:bg-main-900 peer-checked:border-main-900 peer-checked:before:bg-main-900 dark:checked:bg-sub-900 dark:peer-checked:border-sub-900 dark:peer-checked:before:bg-sub-900"
                  v-model="storeFormData.policies.returnpolicy.status"
                  @input="
                    storeFormData.policies.returnpolicy.text
                      ? handleEditedField(true, 'policies', 'returnpolicy')
                      : handleEditedField(false, 'policies', 'returnpolicy')
                  "
                />
                <label
                  htmlFor="returnPolicySwitch"
                  class="before:content[''] absolute top-2/4 -left-2 h-6 w-6 -translate-y-2/4 cursor-pointer rounded-full border border-blue-gray-100 bg-white shadow-md transition-all duration-300 before:absolute before:top-2/4 before:left-2/4 before:block before:h-10 before:w-10 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity hover:before:opacity-10 peer-checked:translate-x-full peer-checked:border-main-900 peer-checked:before:bg-main-900"
                >
                  <div
                    class="inline-block p-5 rounded-full top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4"
                    data-ripple-dark="true"
                  ></div>
                </label>
              </div>
            </div>
            <div class="relative w-full">
              <textarea
                v-if="storeFormData.policies"
                placeholder="We offer return on spoilt goods"
                v-model="storeFormData.policies.returnpolicy.text"
                @input="handleEditedField(true, 'policies', 'returnpolicy')"
                :class="{
                  'peer h-full w-full min-h-28 rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 outline dark:text-gray-200 outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700': true,
                  'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                    returnPolicyError,
                }"
              ></textarea>
            </div>
          </div>
          <div
            class="my-4 px-6"
            :class="{
              'border-l border-sub-900': editedFields.policies?.deliverypolicy,
            }"
          >
            <div class="flex items-center justify-between mb-2 pr-4">
              <div class="flex flex-col items-start portrait:w-3/4 gap-1 mb-4">
                <p
                  class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Delivery Policy
                </p>
                <div class="flex flex-row justify-center items-start gap-1">
                  <i
                    class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                  ></i>
                  <p class="text-xs">Set your store delivery policy</p>
                </div>
              </div>
              <div
                class="relative inline-block w-8 h-6 rounded-full cursor-pointer"
              >
                <input
                  v-if="storeFormData.policies"
                  id="deliveryPolicySwitch"
                  type="checkbox"
                  class="absolute w-8 h-6 transition-colors duration-300 rounded-full appearance-none cursor-pointer peer bg-blue-gray-100 checked:bg-main-900 peer-checked:border-main-900 peer-checked:before:bg-main-900 dark:checked:bg-sub-900 dark:peer-checked:border-sub-900 dark:peer-checked:before:bg-sub-900"
                  v-model="storeFormData.policies.deliverypolicy.status"
                  @input="
                    storeFormData.policies.deliverypolicy.text
                      ? handleEditedField(true, 'policies', 'deliverypolicy')
                      : handleEditedField(false, 'policies', 'deliverypolicy')
                  "
                />
                <label
                  htmlFor="deliveryPolicySwitch"
                  class="before:content[''] absolute top-2/4 -left-2 h-6 w-6 -translate-y-2/4 cursor-pointer rounded-full border border-blue-gray-100 bg-white shadow-md transition-all duration-300 before:absolute before:top-2/4 before:left-2/4 before:block before:h-10 before:w-10 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity hover:before:opacity-10 peer-checked:translate-x-full peer-checked:border-main-900 peer-checked:before:bg-main-900"
                >
                  <div
                    class="inline-block p-5 rounded-full top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4"
                    data-ripple-dark="true"
                  ></div>
                </label>
              </div>
            </div>
            <div class="relative w-full">
              <textarea
                v-if="storeFormData.policies"
                placeholder="We handle the delivery"
                v-model="storeFormData.policies.deliverypolicy.text"
                @input="handleEditedField(true, 'policies', 'deliverypolicy')"
                :class="{
                  'peer h-full w-full min-h-28 rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 outline dark:text-gray-200 outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700': true,
                  'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                    deliveryPolicyError,
                }"
              ></textarea>
            </div>
          </div>
          <div
            v-if="Object.keys(this.editedFields).length > 0"
            class="flex flex-row justify-end items-center gap-4 px-6"
          >
            <button
              @click="handleSubmitForm"
              type="button"
              class="align-middle select-none font-semibold text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-lg portrait:text-sm py-3 px-6 rounded-lg bg-button dark:bg-sub-800 text-white shadow-md shadow-gray-900/10 hover:shadow-lg hover:shadow-main-200 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none"
            >
              Save <i class="fa-solid fa-floppy-disk text-base ml-2"></i>
            </button>
            <button
              @click="resetForm"
              type="button"
              class="select-none rounded-lg border border-main-900 dark:border-white dark:text-white py-3 px-4 text-center portrait:text-sm align-middle text-lg font-semibold text-main-900 transition-all hover:opacity-75 active:opacity-[0.85] disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
            >
              Reset <i class="fa-solid fa-rotate-left text-base ml-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  <template v-else>
    <div
      class="w-full flex flex-col justify-center items-center h-20 mt-6 mb-16"
    >
      <LoadingComponent /></div
  ></template>
</template>

<script>
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import ModalComponent from "@/components/modalComponent.vue";
import CropperComponent from "@/components/cropperComponent.vue";
import { mapActions } from "vuex";
import eventBus from "@/utils/eventBus";
import AddStaffModal from "@/components/addStaffComponent.vue";

export default {
  name: "StoreSettings",
  inject: ["getFileUrl"],
  components: {
    ModalComponent,
    CropperComponent,
    AddStaffModal,
  },
  data() {
    return {
      refreshKey: this.$store.state.storeUpdatedAt || Date.now(),
      notificationSettings: {
        position: "bottom-right",
        transition: "slide",
        autoClose: 1000,
        theme: "auto",
        pauseOnHover: true,
        closeOnClick: true,
      },
      storeFormData: {
        storeNameValue: "",
        storeDiscriptionValue: "",
        storeTaglineValue: "",
        storeEmailValue: "",
        storeUrl: {
          subDomain: "",
          custom_domain: {
            link: "",
            status: false,
          },
        },
        deletedBanner: {
          logo: "",
          portrait: [],
          landscape: [],
        },
        storeBanner: {
          portrait: [],
          landscape: [],
        },
        logoFile: "",
        active: 0,
        policies: {
          returnpolicy: {
            status: false,
            text: "",
          },
          privacypolicy: {
            status: false,
            text: "",
          },
          deliverypolicy: {
            status: false,
            text: "",
          },
        },
      },
      subs: "",
      staffEmail: "",
      addedEmails: [],
      emailSuccess: null,
      isAdding: false,
      isSubmitting: false,
      exceedImageFileSize: false,
      invalidLogoFileType: false,
      exceedPortraitFileSize: false,
      exceedPortraitMaxCount: false,
      invalidPortraitFileType: false,
      exceedLandscapeFileSize: false,
      exceedLandscapeMaxCount: false,
      invalidLandscapeFileType: false,
      taglineError: false,
      emailError: false,
      domainError: false,
      storeNameError: false,
      storeDiscriptionError: false,
      imageUrl: "",
      file: null,
      uploadType: "",
      showModal: false,
      staffModal: false,
      subdomainModal: false,
      addDomainModal: false,
      viewMode: 1,
      aspectRatio: 1,
      storeDetails: null,
      subDomain: "",
      customDomain: "",
      privacyPolicyError: false,
      deliveryPolicyError: false,
      returnPolicyError: false,
      subdomainError: false,
      customdomainError: false,
      editedFields: {},
      themes: [],
      staffs: [],
      removedPortraits: [],
      removedLandscape: [],
      showAllThemes: false,
      domainEdit: false,
      isSaving: false,
      isSubdomainEdited: false,
      showVideoModal: false,
      videoURL: "https://www.youtube.com/watch?v=9yvQTR2siPM",
    };
  },
  props: {
    maxFileSize: {
      type: Number,
      default: 5,
    },
    maxCount: {
      type: Number,
      default: 3,
    },
  },
  provide() {
    return {
      helpVideoURL: this.videoURL,
    };
  },
  beforeUnmount() {
    eventBus.off("open-help-video", this.handleOpenVideo);
  },
  computed: {
    isPortraitBannerEdited() {
      return (
        this.editedFields.storeBanner?.portrait ||
        this.editedFields.deletedBanner?.portrait
      );
    },
    progressMessage() {
      return this.$store.state.domainActivationProgress;
    },

    hasDomain() {
      return this.storeFormData.storeUrl?.custom_domain?.link?.trim() !== "";
    },

    isDomainEnabled() {
      return this.storeFormData.storeUrl?.custom_domain?.status;
    },
    domain() {
      return this.storeFormData.storeUrl?.custom_domain?.link
        ? `https://${this.storeFormData.storeUrl.custom_domain.link}`
        : "";
    },
    selectedStore() {
      return this.$store.state.selectedStore;
    },
    sortedThemes() {
      return this.themes.slice().sort((a, b) => {
        if (a.id === this.storeFormData.active) return -1;
        if (b.id === this.storeFormData.active) return 1;
        return 0;
      });
    },
  },
  watch: {
    selectedStore() {
      this.resetForm();
    },
    "$store.state.storeUpdatedAt": {
      immediate: true,
      handler(val) {
        this.refreshKey = val;
      },
    },
  },
  mounted() {
    this.fetchStoreDetails();
    eventBus.on("open-help-video", this.handleOpenVideo);
  },
  methods: {
    ...mapActions([
      "getStoreDetails",
      "saveStoreSettings",
      "onSaveCustomDomain",
      "addStaff",
    ]),
    validateLength(e, input) {
      const value = e.target.value.trim();
      if (input === "storeDiscriptionValue") {
        const wordCount = value.split(/\s+/).filter(Boolean).length;
        if (wordCount < 10) {
          this.storeDiscriptionError = true;
          toast.warning("Store description too short");
        } else {
          this.handleEditedField(value !== "", input);
          this.storeDiscriptionError = false;
        }
      } else if (input === "storeNameValue") {
        if (e.target.value.trim().length < 3) {
          this.storeNameError = true;
          toast.warning("Store name too short");
        } else {
          this.handleEditedField(value !== "", input);
        }
      }
    },
    handleOpenVideo() {
      this.showVideoModal = true;
    },

    removeEmail(index) {
      this.addedEmails.splice(index, 1);
    },

    async submitEmails() {
      this.isSubmitting = true;

      const response = await this.addStaff(this.addedEmails);

      if (response.success) {
        toast.success(response.message);
      }
      this.isSubmitting = false;
      this.staffModal = false;
    },
    sanitizeDomain(event) {
      this.customDomain = event.target.value
        .toLowerCase()
        .replace(/[^a-z0-9.-]/g, "")
        .replace(/(\.{2,})/g, ".");
    },
    sanitizeSubdomain(event) {
      this.subDomain = event.target.value
        .toLowerCase()
        .replace(/[^a-zA-Z0-9\- ]/g, "")
        .replace(/(\.{2,})/g, ".")
        .replace(/^-+|-+$/g, "");
    },
    copyToClipboard(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          toast.success("IP address copied to clipboard!", {
            position: "top-right",
            autoClose: 2000,
          });
        })
        .catch((err) => {
          console.error("Failed to copy text: ", err);
          toast.error("Failed to copy IP address.", {
            position: "top-right",
            autoClose: 2000,
          });
        });
    },
    async saveCustomDomain() {
      if (!this.customDomain || !this.isValidDomain(this.customDomain)) {
        this.customdomainError = "Please enter a valid domain format.";
        return;
      }

      this.customdomainError = null;
      this.isSaving = true;

      // Initial message
      this.$store.commit("setDomainActivationProgress", "Initializing...");

      try {
        const domainActivation = this.onSaveCustomDomain({
          domain: this.customDomain.trim().toLowerCase(),
        });

        // Ensure we await the promise chain and handle errors properly
        await toast.promise(
          domainActivation
            .then((data) => {
              if (!data.success) {
                return Promise.reject(new Error("Domain Activation Failed"));
              }
              setTimeout(() => {
                this.addDomainModal = false;
              }, 2000);
              this.fetchStoreDetails();
              return data.response;
            })
            .catch((err) => {
              console.error("Domain activation error:", err);
              throw err; // Ensure error propagates to `toast.promise`.
            }),
          {
            success: "Activation successful",
            error: "Activation failed!",
          },
          {
            ...this.notificationSettings,
            autoClose: 3000,
            position: "top-right",
          }
        );
      } catch (error) {
        console.error("Unexpected error:", error);
        // Update progress with an error message
        this.$store.commit(
          "setDomainActivationProgress",
          "An unexpected error occurred. Please try again."
        );
      } finally {
        // Ensure `isSaving` is reset only after everything is completed
        this.isSaving = false;
      }
    },

    async fetchStoreDetails() {
      try {
        const response = await this.getStoreDetails();
        if (response.success) {
          this.storeDetails = response.value[0];
          this.themes = response.themes;
          this.staffs = response.staffs;

          // Parse store_url and policies (with error handling)
          const storeUrl = this.storeDetails.store_url;
          const policies = this.storeDetails.policies;

          // Extract subdomain
          this.subDomain = storeUrl.subDomain || "";

          // Check if subdomain has been edited
          if (!storeUrl.subDomain) {
            this.isSubdomainEdited = false;
          } else if (!this.isValidSubdomain(storeUrl.subDomain)) {
            this.isSubdomainEdited = false;
          } else {
            this.isSubdomainEdited =
              `user-${this.storeDetails.account_id}-store`.toLowerCase() !==
              (storeUrl.subDomain || "").toLowerCase();
          }

          // Update form data
          this.storeFormData = {
            storeNameValue: this.storeDetails.store_name || "",
            storeEmailValue: this.storeDetails.store_email || "",
            storeDiscriptionValue: this.storeDetails.store_description || "",
            storeTaglineValue: this.storeDetails.store_tagline || "",
            logoFile: this.storeDetails.branding?.logo || "",
            active: this.storeDetails.theme || null,
            storeUrl: {
              subDomain: storeUrl.subdomain || "", // Fixed typo here
              custom_domain: {
                link: storeUrl.custom_domain?.link || "",
                status: Boolean(storeUrl.custom_domain?.status),
              },
            },
            storeBanner: {
              portrait: this.storeDetails.branding?.portrait_banner || [],
              landscape: this.storeDetails.branding?.landscape_banner || [],
            },
            policies: {
              returnpolicy: {
                text: policies.returnpolicy?.text || "",
                status: Boolean(policies.returnpolicy?.status),
              },
              privacypolicy: {
                text: policies.privacypolicy?.text || "",
                status: Boolean(policies.privacypolicy?.status),
              },
              deliverypolicy: {
                text: policies.deliverypolicy?.text || "",
                status: Boolean(policies.deliverypolicy?.status),
              },
            },
          };
        }
      } catch (error) {
        console.log("There has been an error: ", error);
      }
    },
    isValidDomain(domain) {
      const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]{1,63}\.)+[a-zA-Z]{2,}$/;
      return domainRegex.test(domain);
    },
    isValidSubdomain(subdomain) {
      const subdomainRegex = /^[a-z0-9-]{3,63}$/;
      return subdomainRegex.test(subdomain);
    },
    resetForm() {
      Object.keys(this.storeFormData).forEach((key) => {
        const keyType = typeof this.storeFormData[key];

        if (keyType === "string") {
          this.storeFormData[key] = "";
        } else if (keyType === "number") {
          this.storeFormData[key] = 0;
        } else if (keyType === "boolean") {
          this.storeFormData[key] = false;
        } else if (Array.isArray(this.storeFormData[key])) {
          this.storeFormData[key] = [];
        } else {
          this.storeFormData[key] = null;
        }
      });
      this.fetchStoreDetails();
      this.editedFields = {};
    },
    saveSubdomain() {
      this.storeFormData.storeUrl.subDomain = this.subDomain;
      this.handleEditedField(true, "storeUrl");
      this.subdomainModal = false;
    },
    handleEditedField(value, field, subField = null) {
      if (!this.editedFields) {
        this.editedFields = {};
      }

      if (!value) {
        if (subField && this.editedFields[field]) {
          delete this.editedFields[field][subField];
          // Clean up empty nested objects
          if (Object.keys(this.editedFields[field]).length === 0) {
            delete this.editedFields[field];
          }
        } else {
          delete this.editedFields[field];
        }
        console.log("Edited fields", this.editedFields);
        return;
      }

      if (subField) {
        this.editedFields[field] = {
          ...(this.editedFields[field] || {}),
          [subField]: value,
        };
      } else {
        this.editedFields[field] = value;
      }

      console.log("Edited fields", this.editedFields);
    },
    async handleSubmitForm() {
      const formData = new FormData();

      // Check and append portrait banners if available
      if (this.storeFormData.storeBanner?.portrait) {
        this.storeFormData.storeBanner.portrait.forEach((file) => {
          if (file instanceof File) {
            formData.append(`storeBanner[portrait]`, file);
          }
        });
      }

      // Check and append landscape banners if available
      if (this.storeFormData.storeBanner?.landscape) {
        this.storeFormData.storeBanner.landscape.forEach((file) => {
          if (file instanceof File) {
            formData.append(`storeBanner[landscape]`, file);
          }
        });
      }

      // Append logoFile if it exists
      if (this.storeFormData.logoFile instanceof File) {
        formData.append("logoFile", this.storeFormData.logoFile);
      }

      // Append deleted logo
      if (this.editedFields.deletedBanner?.logo) {
        formData.append("deletedLogo", this.editedFields.deletedBanner.logo);
      }

      // Append deleted landscape image
      if (this.editedFields.deletedBanner?.landscape) {
        formData.append(
          "deletedBannerLandscape",
          JSON.stringify(this.editedFields.deletedBanner.landscape)
        );
      }

      // Append deleted portrait image
      if (this.editedFields.deletedBanner?.portrait) {
        formData.append(
          "deletedBannerPortrait",
          JSON.stringify(this.editedFields.deletedBanner.portrait)
        );
      }

      // Append other fields (stringified if necessary)
      for (const field in this.editedFields) {
        if (
          typeof this.storeFormData[field] === "object" &&
          field !== "storeBanner" &&
          field !== "deletedBanner"
        ) {
          formData.append(field, JSON.stringify(this.storeFormData[field]));
        } else if (field !== "logoFile" && this.storeFormData[field]) {
          formData.append(field, this.storeFormData[field]);
        }
      }

      // Log FormData content for debugging
      for (const [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(`Key: ${key}, Value:`, {
            name: value.name,
            size: value.size,
            type: value.type,
          });
        } else {
          console.log(`Key: ${key}, Value:`, value);
        }
      }
      const payloadBlob = new Blob([...formData]);
      const payloadByte = payloadBlob.size;
      const sizeInKB = (payloadByte / 1024).toFixed(2); // KB
      const sizeInMB = (sizeInKB / 1024).toFixed(2); // MB
      console.log(`Payload size: ${sizeInKB} KB (${sizeInMB} MB)`);

      // Submit the form data
      try {
        const storeSettingsPromise = this.saveStoreSettings(formData).then(
          (data) => {
            if (!data.success) {
              return Promise.reject(new Error("Update Failed!"));
            }

            if (this.$store.getters.requiresSetup) {
              this.$store.commit("setRequiresSetup", false);
              localStorage.setItem("authToken", this.$store.state.user);
            }
            this.editedFields = {};
            return data.response;
          }
        );

        toast.promise(
          storeSettingsPromise,
          {
            pending: "Updating settings...",
            success: {
              render() {
                return "Settings updated successfully!";
              },
              icon: true,
            },
            error: {
              render() {
                return "Failed to update settings!";
              },
            },
          },
          {
            ...this.notificationSettings,
            autoClose: 3000,
            position: "top-right",
          }
        );
      } catch (error) {
        console.error("There has been an error: ", error);
      }
    },
    activateTheme(themeID) {
      this.storeFormData.active = themeID;
      this.handleEditedField(true, "active");
    },
    removeLogoImage() {
      this.handleEditedField(
        this.storeFormData.logoFile,
        "deletedBanner",
        "logo"
      );
      this.storeFormData.logoFile = "";
    },
    removePortraitImage(index) {
      if (this.storeFormData.storeBanner.portrait.length > 0) {
        const removedFile = this.storeFormData.storeBanner.portrait[index];
        this.removedPortraits.push(removedFile);
        this.handleEditedField(
          this.removedPortraits,
          "deletedBanner",
          "portrait"
        );
        this.storeFormData.storeBanner.portrait.splice(index, 1);
      }
    },
    removeLandscapeImage(index) {
      if (this.storeFormData.storeBanner.landscape.length > 0) {
        const removedFile = this.storeFormData.storeBanner.landscape[index];
        this.removedLandscape.push(removedFile);
        this.handleEditedField(
          this.removedLandscape,
          "deletedBanner",
          "landscape"
        );
        this.storeFormData.storeBanner.landscape.splice(index, 1);
      }
    },
    handleCroppedImage(croppedCanvas) {
      const fileDetails = this.file;
      croppedCanvas.toBlob((blob) => {
        const croppedFile = new File([blob], fileDetails.name, {
          type: fileDetails.type,
        });

        switch (this.uploadType) {
          case "logo":
            this.storeFormData.logoFile = croppedFile;
            this.showModal = false;
            break;
          case "portrait":
            this.storeFormData.storeBanner.portrait.push(croppedFile);
            this.file = null;
            this.showModal = false;
            break;
          case "landscape":
            this.storeFormData.storeBanner.landscape.push(croppedFile);
            this.file = null;
            this.showModal = false;
            break;
        }
      }, fileDetails.type);
    },
    handleCancel() {
      this.imageUrl = "";
      this.file = null;
      this.showModal = false;
    },
    handleCloseDialog() {
      const fileDetails = this.file;
      switch (this.uploadType) {
        case "logo":
          if (fileDetails) {
            this.storeFormData.logoFile = fileDetails;
            this.imageUrl = "";
          }
          this.showModal = false;
          break;

        case "portrait":
          if (fileDetails) {
            this.storeFormData.storeBanner.portrait.push(fileDetails);
            this.file = null;
            this.imageUrl = "";
          }
          this.showModal = false;
          break;

        case "landscape":
          if (fileDetails) {
            this.storeFormData.storeBanner.landscape.push(fileDetails);
            this.file = null;
            this.imageUrl = "";
          }
          this.showModal = false;
          break;

        default:
          this.showModal = false;
          break;
      }
    },
    goback() {
      this.$router.back();
    },
    handlePortraitUpload(event) {
      this.showModal = false;
      const files = event.target.files;
      this.exceedPortraitFileSize = false;
      this.invalidPortraitFileType = false;
      this.uploadType = "";

      for (let file of files) {
        const fileType = file.type;
        if (
          fileType !== "image/jpeg" &&
          fileType !== "image/png" &&
          fileType !== "image/jpg"
        ) {
          this.invalidPortraitFileType = true;
          return;
        }

        const filesizeMb = file.size / (1024 * 1024);
        if (filesizeMb > this.maxFileSize) {
          this.exceedPortraitFileSize = true;
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          this.imageUrl = e.target.result;
          this.uploadType = "portrait";
          this.file = file;
          this.aspectRatio = 9 / 16;
          this.showModal = true;
        };
        reader.readAsDataURL(file);
      }
    },
    handleLandscapeUpload(event) {
      this.showModal = false;
      const files = event.target.files;
      this.exceedLandscapeFileSize = false;
      this.invalidLandscapeFileType = false;
      this.uploadType = "";

      for (let file of files) {
        const fileType = file.type;
        if (
          fileType !== "image/jpeg" &&
          fileType !== "image/png" &&
          fileType !== "image/jpg"
        ) {
          this.invalidLandscapeFileType = true;
          return;
        }

        const filesizeMb = file.size / (1024 * 1024);
        if (filesizeMb > this.maxFileSize) {
          this.exceedLandscapeFileSize = true;
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          this.imageUrl = e.target.result;
          this.uploadType = "landscape";
          this.file = file;
          this.aspectRatio = 16 / 9;
          this.showModal = true;
        };
        reader.readAsDataURL(file);
      }
    },
    handleLogoUpload(event) {
      const files = event.target.files;
      this.exceedImageFileSize = false;
      this.invalidLogoFileType = false;
      this.uploadType = "";

      for (let file of files) {
        const fileType = file.type;
        if (
          fileType !== "image/jpeg" &&
          fileType !== "image/png" &&
          fileType !== "image/jpg"
        ) {
          this.invalidLogoFileType = true;
          return;
        }
        const filesizeMb = file.size / (1024 * 1024);
        if (filesizeMb > this.maxFileSize) {
          this.exceedImageFileSize = true;
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          this.imageUrl = e.target.result;
          this.file = file;
          this.uploadType = "logo";
          this.showModal = true;
          event.target.value = "";
        };
        reader.readAsDataURL(file);
      }
    },
  },
};
</script>
